// AddEmployeeFormUI.tsx
import { SafeArgonSelect } from "0.Mine/0.Components/Onboarding/UI/PlatformFormUI";
import { ImageDropzone } from "0.Mine/0.Components/Reusable/ImageDropzone";
import { Add, Recycling, Check, Delete } from "@mui/icons-material";
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Collapse,
  Fade,
  SelectChangeEvent,
  Chip,
  FormControl,
} from "@mui/material";
import React from "react";

import { NewEmployee } from "../Logic/AddEmployee";

export interface AddEmployeeFormUIProps {
  show: boolean;
  newEmployee: NewEmployee;
  availableServices: string[];
  onFieldChange: (field: keyof NewEmployee, value: any) => void;
  onServiceChange: (index: number, value: string) => void;
  onAddService: () => void;
  handleServiceChange: (event: SelectChangeEvent<string>) => void;
  onRemoveService: (index: number) => void;
  onAddEmployee: () => void;
}

const AddEmployeeFormUI: React.FC<AddEmployeeFormUIProps> = ({
  show,
  newEmployee,
  availableServices,
  onFieldChange,
  handleServiceChange,
  onRemoveService,
  onAddEmployee,
}) => {
  return (
    <Collapse in={show} timeout={500}>
      <Fade in={show} timeout={800}>
        <Paper
          elevation={4}
          sx={{
            p: 3,
            borderRadius: 2,
            mb: 3,
            bgcolor: "primary.light",
            color: "primary.contrastText",
            borderLeft: 4,
            borderColor: "primary.main",
          }}
        >
          <Typography variant="h6" fontWeight="bold" display="flex" alignItems="center" gap={1}>
            <Add /> Dettagli nuovo collaboratore
          </Typography>

          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={4}>
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <ImageDropzone
                  onDropFile={(acceptedFiles) => {
                    onFieldChange("photo", acceptedFiles);
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                size="small"
                label="Nome"
                value={newEmployee.name}
                onChange={(e) => onFieldChange("name", e.target.value)}
                variant="outlined"
                required
                sx={{ mb: 2, bgcolor: "background.paper" }}
              />
              <TextField
                fullWidth
                size="small"
                label="Ruolo"
                value={newEmployee.role}
                onChange={(e) => onFieldChange("role", e.target.value)}
                variant="outlined"
                required
                sx={{ bgcolor: "background.paper" }}
              />
              <TextField
                fullWidth
                size="small"
                label="email"
                value={newEmployee.email}
                onChange={(e) => onFieldChange("email", e.target.value)}
                variant="outlined"
                required
                sx={{ bgcolor: "background.paper" }}
              />
            </Grid>
          </Grid>

          <Typography fontWeight="bold" mt={3} mb={1}>
            Servizi Offerti:
          </Typography>

          {/* Service selection dropdown */}
          <FormControl fullWidth size="small" sx={{ mb: 2, bgcolor: "background.paper" }}>
            <SafeArgonSelect
              placeholder="Aggiungi Servizio"
              options={availableServices.map((service) => ({
                value: service,
                label: service,
              }))}
              onChange={(selectedOption: any) => {
                // Create a synthetic event that mimics the MUI SelectChangeEvent.
                const syntheticEvent = {
                  target: { value: selectedOption?.value || "" },
                } as SelectChangeEvent<string>;
                handleServiceChange(syntheticEvent);
              }}
              value={null} // always empty so that selection resets after each change
            />
          </FormControl>

          {/* Display selected services as chips */}
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
            {newEmployee.services.filter(Boolean).map((service, index) => (
              <Chip
                key={index}
                label={service}
                onDelete={() => onRemoveService(index)}
                color="primary"
                variant="outlined"
                sx={{ bgcolor: "background.paper" }}
                deleteIcon={<Delete />}
              />
            ))}
          </Box>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
            <Button
              variant="contained"
              color="success"
              onClick={onAddEmployee}
              disabled={!newEmployee.name || !newEmployee.role}
              startIcon={<Check />}
            >
              Salva
            </Button>
          </Box>
        </Paper>
      </Fade>
    </Collapse>
  );
};

export default AddEmployeeFormUI;
